import { useSyncExternalStore } from "react";
import { BoardAction } from "../dtos";

/**
 * §BannerAboveTabs: the next-action banner now lives ABOVE the lead-page tab bar (mirroring the
 * orange start-flow banner), so it renders OUTSIDE the Nawy Now tab's React tree — while the step
 * it points at is rendered INSIDE it. This tiny store is the one-way channel between them: the
 * banner's review modal publishes "focus this step", the board picks it up and expands/scrolls to it.
 *
 * A module-scoped store (rather than a context provider threaded through both lead layouts) keeps
 * the two layouts untouched apart from mounting the banner, and there is only ever one lead page
 * on screen. Requests are one-shot: the board consumes and clears, so re-mounting never replays a
 * stale jump.
 */
let request: BoardAction | null = null;
const listeners = new Set<() => void>();

const emit = (): void => {
  listeners.forEach((listener) => listener());
};

const subscribe = (listener: () => void): (() => void) => {
  listeners.add(listener);
  return () => {
    listeners.delete(listener);
  };
};

/** Ask the board to reveal a step (banner → tab). Switching to the tab is the caller's job. */
export const requestBoardFocus = (action: BoardAction): void => {
  request = action;
  emit();
};

/** The board consumed the request — clears it so it fires exactly once. */
export const clearBoardFocus = (): void => {
  if (!request) return;
  request = null;
  emit();
};

export const useBoardFocusRequest = (): BoardAction | null =>
  useSyncExternalStore(
    subscribe,
    () => request,
    () => null,
  );
